import React, { useState, useEffect, useMemo } from 'react';
import html2pdf from 'html2pdf.js';
import { useNavigate } from 'react-router-dom';
import {
  Calculator,
  FileText,
  Download,
  Users,
  DollarSign,
  X,
  Calendar,
  Building,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  ChevronRight,
  Loader2,
  BarChart3,
  Shield,
} from 'lucide-react';
import { Button } from '@mui/material';
import config from '../config/config';
import axios from 'axios';

/* ─── Global Quicksand font injection ─── */
const fontStyle = document.createElement('style');
fontStyle.textContent = `
  @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap');
  * { font-family: 'Quicksand', sans-serif !important; }

  :root {
    --white: #ffffff;
    --bg: #f7f8fc;
    --surface: #ffffff;
    --border: #e8eaf2;
    --border-hover: #c5cae9;
    --text-primary: #1a1d2e;
    --text-secondary: #5c6280;
    --text-muted: #9ca3af;
    --blue: #4361ee;
    --blue-light: #eef0fd;
    --blue-mid: #7b8ff5;
    --green: #06c270;
    --green-light: #e6faf3;
    --purple: #7c3aed;
    --purple-light: #f3effe;
    --amber: #f59e0b;
    --amber-light: #fffbeb;
    --red: #ef4444;
    --red-light: #fef2f2;
    --shadow-sm: 0 1px 3px rgba(67,97,238,.06), 0 1px 2px rgba(0,0,0,.04);
    --shadow-md: 0 4px 16px rgba(67,97,238,.10), 0 2px 4px rgba(0,0,0,.04);
    --shadow-lg: 0 12px 40px rgba(67,97,238,.14), 0 4px 8px rgba(0,0,0,.06);
    --radius: 14px;
    --radius-sm: 8px;
    --radius-lg: 20px;
  }

  body { background: var(--bg) !important; }

  /* Shimmer animation for loading */
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .animate-fadeInUp { animation: fadeInUp 0.35s ease both; }

  /* Floating label input */
  .float-label-wrap { position: relative; }
  .float-label-wrap input[type="month"] {
    width: 100%;
    padding: 14px 16px 6px 16px;
    border: 1.5px solid var(--border);
    border-radius: var(--radius-sm);
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    background: var(--white);
    outline: none;
    transition: border-color .2s, box-shadow .2s;
    appearance: none;
    -webkit-appearance: none;
  }
  .float-label-wrap input[type="month"]:focus {
    border-color: var(--blue);
    box-shadow: 0 0 0 3px rgba(67,97,238,.12);
  }
  .float-label-wrap label {
    position: absolute;
    top: 50%;
    left: 16px;
    transform: translateY(-50%);
    font-size: 14px;
    font-weight: 500;
    color: var(--text-muted);
    pointer-events: none;
    transition: all .18s ease;
  }
  .float-label-wrap input[type="month"]:not(:placeholder-shown) + label,
  .float-label-wrap input[type="month"]:focus + label,
  .float-label-wrap.has-value label {
    top: 6px;
    transform: translateY(0);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--blue);
  }

  /* Scrollbar */
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: var(--border-hover); border-radius: 99px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--blue-mid); }

  /* Table polish */
  .payroll-table thead th {
    background: var(--bg) !important;
    color: var(--text-secondary) !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    letter-spacing: .08em !important;
    text-transform: uppercase !important;
    padding: 14px 18px !important;
    white-space: nowrap;
    border-bottom: 1.5px solid var(--border) !important;
  }
  .payroll-table tbody td {
    padding: 13px 18px !important;
    font-size: 13.5px !important;
    font-weight: 500 !important;
    color: var(--text-primary) !important;
    border-bottom: 1px solid var(--border) !important;
    white-space: nowrap;
    vertical-align: middle;
  }
  .payroll-table tbody tr:hover td { background: var(--blue-light) !important; }
  .payroll-table tfoot td {
    background: var(--bg) !important;
    font-weight: 700 !important;
    font-size: 13px !important;
    color: var(--text-primary) !important;
    padding: 14px 18px !important;
    border-top: 2px solid var(--border) !important;
    white-space: nowrap;
  }

  /* Pill badge */
  .pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
  }
`;
document.head.appendChild(fontStyle);

/* ─── Sub-components ─── */

const StatCard = ({ title, value, icon: Icon, accent, subtitle }) => {
  const accents = {
    blue: { bg: 'var(--blue-light)', text: 'var(--blue)', icon: '#4361ee' },
    green: { bg: 'var(--green-light)', text: 'var(--green)', icon: '#06c270' },
    purple: { bg: 'var(--purple-light)', text: 'var(--purple)', icon: '#7c3aed' },
    amber: { bg: 'var(--amber-light)', text: 'var(--amber)', icon: '#f59e0b' },
  };
  const a = accents[accent] || accents.blue;

  return (
    <div style={{
      background: 'var(--white)',
      border: '1.5px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: '24px',
      boxShadow: 'var(--shadow-sm)',
      transition: 'box-shadow .2s, transform .2s',
      cursor: 'default',
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>{title}</p>
          <p style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>{value}</p>
          {subtitle && <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, fontWeight: 500 }}>{subtitle}</p>}
        </div>
        <div style={{ background: a.bg, borderRadius: 12, padding: 12, flexShrink: 0 }}>
          <Icon size={20} color={a.icon} strokeWidth={2.2} />
        </div>
      </div>
    </div>
  );
};

const TabButton = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: '8px 22px',
      borderRadius: 10,
      border: 'none',
      cursor: 'pointer',
      fontSize: 13,
      fontWeight: 700,
      transition: 'all .18s ease',
      background: active ? 'var(--white)' : 'transparent',
      color: active ? 'var(--blue)' : 'rgba(255,255,255,0.7)',
      boxShadow: active ? 'var(--shadow-sm)' : 'none',
      letterSpacing: '.01em',
      textTransform: 'capitalize',
    }}
  >
    {label}
  </button>
);

const PrimaryBtn = ({ children, onClick, disabled, color = 'blue', size = 'md', icon: Icon }) => {
  const colors = {
    blue: { bg: 'var(--blue)', hover: '#3451d1' },
    green: { bg: 'var(--green)', hover: '#05a85f' },
    red: { bg: 'var(--red)', hover: '#dc2626' },
  };
  const c = colors[color] || colors.blue;
  const padding = size === 'sm' ? '7px 14px' : '10px 20px';
  const fontSize = size === 'sm' ? 12 : 13;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding, fontSize, fontWeight: 700,
        background: disabled ? 'var(--border)' : c.bg,
        color: disabled ? 'var(--text-muted)' : '#fff',
        border: 'none', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background .15s, transform .15s, box-shadow .15s',
        boxShadow: disabled ? 'none' : '0 2px 8px rgba(67,97,238,.25)',
        letterSpacing: '.02em',
      }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = c.hover; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
      onMouseLeave={e => { if (!disabled) { e.currentTarget.style.background = c.bg; e.currentTarget.style.transform = 'translateY(0)'; } }}
    >
      {Icon && <Icon size={14} strokeWidth={2.5} />}
      {children}
    </button>
  );
};

/* ─── Month Picker with floating label + no pre-filled zero bug ─── */
const MonthPicker = ({ value, onChange, label = 'Select month', style = {} }) => {
  const [focused, setFocused] = useState(false);
  const hasValue = !!value;

  return (
    <div className={`float-label-wrap ${hasValue ? 'has-value' : ''}`} style={{ minWidth: 200, ...style }}>
      <input
        type="month"
        value={value || ''}
        placeholder=" "
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={e => onChange(e.target.value)}
        style={{ paddingTop: hasValue || focused ? '18px' : '12px' }}
      />
      <label>{label}</label>
    </div>
  );
};

/* ─── Modal wrapper ─── */
const Modal = ({ title, onClose, children, width = 480 }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(26,29,46,.45)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  }}>
    <div style={{
      background: 'var(--white)', borderRadius: 'var(--radius-lg)',
      width: '100%', maxWidth: width,
      boxShadow: 'var(--shadow-lg)',
      animation: 'fadeInUp .25s ease both',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '22px 28px', borderBottom: '1.5px solid var(--border)' }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center' }}>
          <X size={18} />
        </button>
      </div>
      <div style={{ padding: '24px 28px' }}>{children}</div>
    </div>
  </div>
);

/* ─── FormField wrapper ─── */
const FormField = ({ label, children }) => (
  <div style={{ marginBottom: 18 }}>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 8 }}>{label}</label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', padding: '11px 14px',
  border: '1.5px solid var(--border)', borderRadius: 10,
  fontSize: 14, fontWeight: 600, color: 'var(--text-primary)',
  background: 'var(--white)', outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color .2s, box-shadow .2s',
};

/* ─── Compliance row ─── */
const ComplianceRow = ({ label, value, accent, onClick }) => {
  const accents = {
    blue: { bg: 'var(--blue-light)', val: 'var(--blue)' },
    purple: { bg: 'var(--purple-light)', val: 'var(--purple)' },
    red: { bg: 'var(--red-light)', val: 'var(--red)' },
    amber: { bg: 'var(--amber-light)', val: 'var(--amber)' },
    green: { bg: 'var(--green-light)', val: 'var(--green)' },
  };
  const a = accents[accent] || accents.blue;
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 18px', borderRadius: 12, background: a.bg,
      cursor: 'pointer', transition: 'filter .15s',
    }}
      onMouseEnter={e => e.currentTarget.style.filter = 'brightness(.96)'}
      onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}
    >
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {value
          ? <span style={{ fontSize: 13, fontWeight: 700, color: a.val }}>{value}</span>
          : <CheckCircle size={18} color="var(--green)" strokeWidth={2.5} />
        }
        <ChevronRight size={14} color="var(--text-muted)" />
      </div>
    </div>
  );
};

/* ════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════ */
const KenyanPayroll = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('payroll');
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [nssfTier, setNssfTier] = useState('both');
  const [employees, setEmployees] = useState([]);
  const [payrollData, setPayrollData] = useState([]);
  const [benefitsDeductions, setBenefitsDeductions] = useState([]);
  const [benefitsDeductions2, setBenefitsDeductions2] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  /* Filter month — empty string = no filter applied */
  const [filterMonth, setFilterMonth] = useState('');

  const handleSearch = () => {
    if (filterMonth) {
      fetchWithCache('payrollData', `${config.API_URL}/api/pull_payrolls/` + filterMonth, setPayrollData, token);
    } else {
      fetchWithCache('payrollData', `${config.API_URL}/api/pull_payrolls`, setPayrollData, token);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/api/pullemployees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch employees');
    }
  };

  const checkTab = async (tab) => {
    if (tab === 'employees' || tab === 'salaries') navigate('/employees');
  };

  const fetchBenefitsDeductions = async (companyId = 1) => {
    try {
      const response = await fetch(`${config.API_URL}/api/benefits-deductions/${companyId}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      const data = await response.json();
      setBenefitsDeductions(data.data || []);
    } catch (err) {
      setError('Failed to fetch benefits/deductions');
    }
  };

  const processPayroll = async () => {
    setProcessing(true);
    setError(null);
    const [year, month] = selectedMonth.split('-');
    const companyId = 1;
    try {
      const response = await fetch(
        `${config.API_URL}/api/payroll/process/${companyId}/${year}/${month}?nssfTier=${nssfTier}`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } }
      );
      const result = await response.json();
      if (result.status === 'success') {
        const payrollResponse = await fetch(`${config.API_URL}/api/payroll/${companyId}/${year}/${month}`, {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
        });
        const payrollResult = await payrollResponse.json();
        setPayrollData(payrollResult.data || []);
        setShowProcessModal(false);
        fetchPayrollData();
      } else {
        setError(result.message || 'Failed to process payroll');
      }
    } catch (err) {
      setError('Failed to process payroll');
    } finally {
      setProcessing(false);
    }
  };

  const TTL = 1000 * 0 * 60;
  const getCachedData = (key) => {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    try {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp > TTL) { localStorage.removeItem(key); setLoading(false); return null; }
      setLoading(false);
      return Array.isArray(data) ? data : [];
    } catch (e) { setLoading(false); return null; }
  };
  const setCachedData = (key, data) => {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
    setLoading(false);
  };
  const fetchWithCache = async (key, url, setter, token) => {
    const cached = getCachedData(key);
    if (cached) { setter(cached); return; }
    setLoading(true);
    try {
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' } });
      const result = response.data.data || response.data || [];
      setSelectedMonth(response.data.payrollMonth);
      setter(Array.isArray(result) ? result : []);
      setCachedData(key, result);
    } catch (error) { setter([]); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!token) { setError('No token found. Please log in.'); return; }
    fetchWithCache('payrollData', `${config.API_URL}/api/pull_payrolls`, setPayrollData, token);
    fetchEmployees();
    fetchBenefitsDeductions();
  }, [token]);

  const fetchPayrollData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_URL}/api/pull_payrolls`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { data, payrollMonth, benefits_deductions } = response.data;
      setPayrollData(data);
      setBenefitsDeductions2(benefits_deductions || []);
    } catch (err) {
      console.error('Error fetching payroll:', err);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) fetchPayrollData(); }, [token]);

  const generateReport = async (type) => {
    setGenerating(true);
    setError(null);
    const [year, month] = selectedMonth.split('-');
    const companyId = 1;
    let endpoint = config.API_URL;
    let filename = `${type}_report_${year}_${month}`;

    switch (type) {
      case 'nssf': endpoint += `/api/payroll/nssf/${companyId}/${year}/${month}`; break;
      case 'nita': endpoint += `/api/payroll/nssf/${companyId}/${year}/${month}`; break;
      case 'shif': endpoint += `/api/payroll/shif/${companyId}/${year}/${month}`; break;
      case 'housing': endpoint += `/api/payroll/housing/${companyId}/${year}/${month}`; break;
      case 'p9': endpoint += `/api/payroll/p9/${companyId}/${year}`; filename = `${type}_report_${year}`; break;
      case 'payslips': endpoint += `/api/payroll/payslips/${companyId}/${year}/${month}`; break;
      case 'paye': endpoint += `/api/payroll/payslips/${companyId}/${year}/${month}`; break;
      case 'summary': endpoint += `/api/payroll/summary/${companyId}/${year}/${month}`; break;
      default: setError(`Invalid report type: ${type}`); setGenerating(false); return;
    }

    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) throw new Error(`Expected JSON`);
      const data = await response.json();

      const reportData = (data.status === 'success' && Array.isArray(data.data) && data.data.length > 0)
        ? data.data.map(item => ({
          user_id: item.user_id || '', name: item.name || 'N/A', kra_pin: item.kra_pin || 'N/A',
          nssf_number: item.nssf_number || 'N/A', sha_number: item.sha_number || 'N/A',
          employee_id: item.employee_id || item.user_id || 'N/A', department: item.department || 'N/A',
          gross_salary: Number(item.gross_salary || 0), paye: Number(item.paye || 0),
          shif: Number(item.shif || Math.max((item.gross_salary || 0) * 0.0275, 300)),
          nssf: Number(item.nssf || Math.min((item.gross_salary || 0) * 0.06, 8000)),
          housing_levy: Number(item.housing_levy || (item.gross_salary || 0) * 0.015),
          net_pay: Number(item.net_pay || 0), created_at: item.created_at || new Date().toISOString(), ...item,
        }))
        : payrollData.map(item => ({
          user_id: item.user_id || '', name: item.name || 'N/A', kra_pin: item.kra_pin || 'N/A',
          nssf_number: item.nssf_number || 'N/A', sha_number: item.sha_number || 'N/A',
          employee_id: item.employee_id || item.user_id || 'N/A', department: item.department || 'N/A',
          gross_salary: Number(item.gross_salary || 0), paye: Number(item.paye || 0),
          shif: Number(item.shif || Math.max((item.gross_salary || 0) * 0.0275, 300)),
          nssf: Number(item.nssf || Math.min((item.gross_salary || 0) * 0.06, 8000)),
          housing_levy: Number(item.housing_levy || (item.gross_salary || 0) * 0.015),
          net_pay: Number(item.net_pay || 0), created_at: item.created_at || new Date().toISOString(), ...item,
        }));

      if (reportData.length === 0) throw new Error(`No valid data for ${type} report`);

      setGenerating(false);

      const openMap = {
        summary: `/payroll_reports/${year}-${month}/${token}`,
        paye: `/paye/${year}-${month}/${token}`,
        shif: `/shif/${year}-${month}/${token}`,
        nssf: `/nssf/${year}-${month}/${token}`,
        nita: `/nita/${year}-${month}/${token}`,
        housing: `/housing_levy/${year}-${month}/${token}`,
      };
      if (openMap[type]) window.open(config.API_URL + openMap[type]);

    } catch (err) {
      setError(`Failed to generate ${type} report: ${err.message}`);
    } finally {
      setGenerating(false);
      setShowReportsModal(false);
    }
  };

  const closePayroll = () => window.close();

  const downloadPayslip = async (employee) => {
    setGenerating(true);
    setError(null);
    const [year, month] = selectedMonth.split('-');
    try {
      window.open(`${config.API_URL}/payslip/${year}-${month}/${employee.user_id}/${token}`);
    } catch (err) {
      setError(`Failed to generate payslip: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const generateReportContent = (type, data, year, month) => {
    const safeData = Array.isArray(data) ? data : [];
    const monthName = new Date(`${year}-${month}-01`).toLocaleString('default', { month: 'long' });
    const date = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const companyHeader = `<div style="text-align:center;margin-bottom:.5in"><div style="font-size:16px;font-weight:500">Your Company Name</div><div style="font-size:12px">P.O. Box 12345, Nairobi, Kenya</div></div>`;

    const reportConfigs = {
      summary: {
        title: `Payroll Summary - ${monthName} ${year}`,
        headers: ['Employee', 'Gross Salary', 'PAYE', 'SHIF', 'NSSF', 'Housing Levy', 'Net Pay'],
        rowMapper: item => [item.name || 'N/A', `KES ${Number(item.gross_salary || 0).toLocaleString()}`, `KES ${Number(item.paye || 0).toLocaleString()}`, `KES ${Number(item.shif || Math.max((item.gross_salary || 0) * 0.0275, 300)).toLocaleString()}`, `KES ${Number(item.nssf || Math.min((item.gross_salary || 0) * 0.06, 8000)).toLocaleString()}`, `KES ${Number(item.housing_levy || (item.gross_salary || 0) * 0.015).toLocaleString()}`, `KES ${Number(item.net_pay || 0).toLocaleString()}`],
        totals: ['Total', `KES ${safeData.reduce((s, i) => s + Number(i.gross_salary || 0), 0).toLocaleString()}`, `KES ${safeData.reduce((s, i) => s + Number(i.paye || 0), 0).toLocaleString()}`, '', '', '', `KES ${safeData.reduce((s, i) => s + Number(i.net_pay || 0), 0).toLocaleString()}`],
      },
    };
    const cfg = reportConfigs[type] || reportConfigs.summary;
    return `<style>body{font-family:Arial,sans-serif;font-size:12px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f4f4f4;font-weight:bold}.total{font-weight:bold;background:#f9f9f9}</style>${companyHeader}<h2 style="text-align:center">${cfg.title}</h2><table><thead><tr>${cfg.headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${safeData.map(item => `<tr>${cfg.rowMapper(item).map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody><tfoot><tr class="total">${cfg.totals.map(c => `<td>${c}</td>`).join('')}</tr></tfoot></table><div style="text-align:center;font-size:10px;margin-top:.2in">Generated on ${date}</div>`;
  };

  const downloadPDF = async () => {
    setGenerating(true);
    setError(null);
    try {
      const element = document.getElementById('payroll-table');
      if (!element) throw new Error('Payroll table element not found');
      const opt = {
        margin: 0.2, filename: `payroll-summary-${new Date().toISOString().slice(0, 10)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' },
      };
      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      setError(`Failed to generate payroll PDF: ${err.message}`);
    } finally { setGenerating(false); }
  };

  const {
    totalEmployees, totalGross, totalloan, totaladvance, totalNet, totalBasic,
    totalDeductions, totalPaye, totalShif, totalCharge, totalNssf, totalHousing,
    benefitTotals, deductionTotals,
  } = useMemo(() => {
    const safePayroll = Array.isArray(payrollData) ? payrollData : [];
    const totalEmployees = Array.isArray(employees) ? employees.length : 0;
    const totalGross = safePayroll.reduce((s, e) => s + Number(e.gross_salary || 0), 0);
    const totalBasic = safePayroll.reduce((s, e) => s + Number(e.basic_salary || 0), 0);
    const totalloan = safePayroll.reduce((s, e) => s + Number(e.loan || 0), 0);
    const totaladvance = safePayroll.reduce((s, e) => s + Number(e.advance || 0), 0);
    const totalNet = safePayroll.reduce((s, e) => s + Number(e.net_pay || 0), 0);
    const totalPaye = safePayroll.reduce((s, e) => s + Number(e.paye || 0), 0);
    const totalCharge = safePayroll.reduce((s, e) => s + Number(e.bank_transfer_charges || 0), 0);
    const totalShif = safePayroll.reduce((s, e) => s + Number(e.shif || Math.max((e.gross_salary || 0) * 0.0275, 300)), 0);
    const totalNssf = safePayroll.reduce((s, e) => s + Number(e.nssf || Math.min((e.gross_salary || 0) * 0.06, 8000)), 0);
    const totalHousing = safePayroll.reduce((s, e) => s + Number(e.housing_levy || (e.gross_salary || 0) * 0.015), 0);
    const totalDeductions = totalPaye + totalShif + totalNssf + totalHousing + totalCharge;
    const benefitTotals = benefitsDeductions.filter(bd => bd.type === 'benefit').map(bd => ({ name: bd.name, total: safePayroll.reduce((s, e) => s + Number(e[bd.name] || e.details?.[bd.name] || 0), 0) }));
    const deductionTotals = benefitsDeductions.filter(bd => bd.type === 'deduction').map(bd => ({ name: bd.name, total: safePayroll.reduce((s, e) => s + Number(e[bd.name] || e.details?.[bd.name] || 0), 0) }));
    return { totalEmployees, totalGross, totalloan, totaladvance, totalNet, totalBasic, totalPaye, totalShif, totalNssf, totalCharge, totalHousing, totalDeductions, benefitTotals, deductionTotals };
  }, [payrollData, employees, benefitsDeductions]);

  /* ─── Render ─── */
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>

      {/* ── Top Nav ── */}
      <div style={{
        background: 'var(--white)',
        borderBottom: '1.5px solid var(--border)',
        boxShadow: '0 2px 12px rgba(67,97,238,.06)',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 70 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ background: 'var(--blue-light)', borderRadius: 12, padding: 10 }}>
                <Building size={22} color="var(--blue)" strokeWidth={2.2} />
              </div>
              <div>
                <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Payroll Management</h1>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, fontWeight: 500 }}>Kenya PAYE · NSSF · SHIF · Housing Levy</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <PrimaryBtn icon={Calculator} onClick={() => setShowProcessModal(true)}>Process Payroll</PrimaryBtn>
              <PrimaryBtn icon={FileText} color="green" onClick={() => setShowReportsModal(true)}>Reports</PrimaryBtn>
              <PrimaryBtn icon={X} color="red" onClick={closePayroll}>Close</PrimaryBtn>
            </div>
          </div>
        </div>
      </div>

      {/* ── Tab Bar ── */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 32px 0' }}>
        <div style={{
          display: 'inline-flex', gap: 4, padding: 6,
          background: 'var(--text-primary)', borderRadius: 14,
          boxShadow: 'var(--shadow-md)',
        }}>
          {['payroll', 'employees', 'compliance'].map(tab => (
            <TabButton
              key={tab}
              label={tab}
              active={activeTab === tab}
              onClick={() => { setActiveTab(tab); checkTab(tab); }}
            />
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 32px 48px' }}>

        {/* Error banner */}
        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--red-light)', border: '1.5px solid #fecaca',
            borderRadius: 12, padding: '14px 18px', marginBottom: 20,
            fontSize: 13, fontWeight: 600, color: 'var(--red)',
          }}>
            <AlertCircle size={16} strokeWidth={2.5} />
            {error}
          </div>
        )}

        {/* ── PAYROLL TAB ── */}
        {activeTab === 'payroll' && (
          <div className="animate-fadeInUp" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16 }}>
              <StatCard title="Total Employees" value={totalEmployees} icon={Users} accent="blue" subtitle="Active employees" />
              <StatCard title="Gross Payroll" value={`KES ${totalGross.toLocaleString()}`} icon={TrendingUp} accent="green" subtitle="Most recent payroll" />
              <StatCard title="Net Payroll" value={`KES ${totalNet.toLocaleString()}`} icon={DollarSign} accent="purple" subtitle="Take-home pay" />
            </div>

            {/* Table card */}
            <div style={{ background: 'var(--white)', borderRadius: 'var(--radius)', border: '1.5px solid var(--border)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>

              {/* Card header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1.5px solid var(--border)', flexWrap: 'wrap', gap: 14 }}>
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Current Processed Payroll</h2>
                  {selectedMonth && (
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0', fontWeight: 500 }}>
                      Showing: {new Date(selectedMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>

                {/* Filter row — fixed UX: starts empty, placeholder shows, no forced "0" */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <MonthPicker
                    value={filterMonth}
                    onChange={setFilterMonth}
                    label="Filter by month"
                  />
                  <PrimaryBtn onClick={handleSearch} disabled={loading}>
                    {loading ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                    Filter
                  </PrimaryBtn>
                  <PrimaryBtn icon={Download} color="green" size="md" onClick={downloadPDF} disabled={generating}>
                    {generating ? 'Generating…' : 'Export PDF'}
                  </PrimaryBtn>
                </div>
              </div>

              {/* Table */}
              <div style={{ overflowX: 'auto' }} id="payroll-table">
                {payrollData.length > 0 ? (
                  <table className="payroll-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Basic</th>
                        {benefitsDeductions2.filter(bd => bd.type === 'benefit').map(bd => <th key={bd.id}>{bd.name}</th>)}
                        <th>Gross</th>
                        <th>PAYE</th>
                        <th>SHIF</th>
                        <th>NSSF</th>
                        <th>Housing Levy</th>
                        <th>Salary Advance</th>
                        <th>Loans</th>
                        {benefitsDeductions2.filter(bd => bd.type === 'deduction').map(bd => <th key={bd.id}>{bd.name}</th>)}
                        <th>Bank Charge</th>
                        <th>Net Pay</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrollData.map(item => (
                        <tr key={item.user_id}>
                          <td>
                            <div style={{ fontWeight: 700, fontSize: 13 }}>{item.name || 'N/A'}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginTop: 2 }}>{item.payroll_index || 'N/A'}</div>
                          </td>
                          <td>KES {Number(item.basic_salary || 0).toLocaleString()}</td>
                          {benefitsDeductions2.filter(bd => bd.type === 'benefit').map(bd => (
                            <td key={bd.id}>KES {Number(item.benefit_amounts?.[bd.id] || 0).toLocaleString()}</td>
                          ))}
                          <td style={{ fontWeight: 600 }}>KES {Number(item.gross_salary || 0).toLocaleString()}</td>
                          <td>KES {Number(item.paye || 0).toLocaleString()}</td>
                          <td>KES {Number(item.shif || 0).toLocaleString()}</td>
                          <td>KES {Number(item.nssf || 0).toLocaleString()}</td>
                          <td>KES {Number(item.housing_levy || 0).toLocaleString()}</td>
                          <td>KES {Number(item.advance || 0).toLocaleString()}</td>
                          <td>KES {Number(item.loan || 0).toLocaleString()}</td>
                          {benefitsDeductions2.filter(bd => bd.type === 'deduction').map(bd => (
                            <td key={bd.id} style={{ color: 'var(--red)' }}>(KES {Number(item.benefit_amounts?.[bd.id] || 0).toLocaleString()})</td>
                          ))}
                          <td>KES {Number(item.bank_transfer_charges || 0).toLocaleString()}</td>
                          <td style={{ color: 'var(--green)', fontWeight: 700 }}>KES {Number(item.net_pay || 0).toLocaleString()}</td>
                          <td>
                            <button
                              onClick={() => downloadPayslip(item)}
                              disabled={generating}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '6px 12px', borderRadius: 8, border: '1.5px solid var(--blue-light)',
                                background: 'var(--blue-light)', color: 'var(--blue)',
                                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                                transition: 'background .15s',
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = '#dde3fc'}
                              onMouseLeave={e => e.currentTarget.style.background = 'var(--blue-light)'}
                            >
                              <Download size={12} strokeWidth={2.5} />
                              Payslip
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Totals</td>
                        <td>KES {totalBasic.toLocaleString()}</td>
                        {benefitTotals.map(b => <td key={b.name}>KES {b.total.toLocaleString()}</td>)}
                        <td>KES {totalGross.toLocaleString()}</td>
                        <td>KES {totalPaye.toLocaleString()}</td>
                        <td>KES {totalShif.toLocaleString()}</td>
                        <td>KES {totalNssf.toLocaleString()}</td>
                        <td>KES {totalHousing.toLocaleString()}</td>
                        <td>KES {totaladvance.toLocaleString()}</td>
                        <td>KES {totalloan.toLocaleString()}</td>
                        {deductionTotals.map(d => <td key={d.name}>KES {d.total.toLocaleString()}</td>)}
                        <td>KES {totalCharge.toLocaleString()}</td>
                        <td>KES {totalNet.toLocaleString()}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <div style={{ background: 'var(--blue-light)', borderRadius: '50%', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                      <FileText size={28} color="var(--blue)" />
                    </div>
                    <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 6px' }}>No payroll data available</p>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Click "Process Payroll" to get started</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── EMPLOYEES TAB ── */}
        {activeTab === 'employees' && (
          <div className="animate-fadeInUp" style={{ background: 'var(--white)', borderRadius: 'var(--radius)', border: '1.5px solid var(--border)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1.5px solid var(--border)' }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Employee Management</h2>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="payroll-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Basic Salary</th>
                    <th>Bank</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map(employee => (
                    <tr key={employee.id}>
                      <td>
                        <div style={{ fontWeight: 700 }}>{employee.name || 'N/A'}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{employee.employee_id || 'N/A'}</div>
                      </td>
                      <td>{employee.department || 'N/A'}</td>
                      <td>KES {Number(employee.basic_salary || 0).toLocaleString()}</td>
                      <td>{employee.bank || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── COMPLIANCE TAB ── */}
        {activeTab === 'compliance' && (
          <div className="animate-fadeInUp" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 20 }}>
            <div style={{ background: 'var(--white)', borderRadius: 'var(--radius)', border: '1.5px solid var(--border)', boxShadow: 'var(--shadow-sm)', padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div style={{ background: 'var(--blue-light)', borderRadius: 10, padding: 8 }}>
                  <Shield size={18} color="var(--blue)" strokeWidth={2.2} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>KRA Compliance</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <ComplianceRow label="PAYE Returns" accent="blue" onClick={() => generateReport('paye')} />
                <ComplianceRow label="P9 Forms" accent="green" onClick={() => generateReport('p9')} />
              </div>
            </div>

            <div style={{ background: 'var(--white)', borderRadius: 'var(--radius)', border: '1.5px solid var(--border)', boxShadow: 'var(--shadow-sm)', padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div style={{ background: 'var(--purple-light)', borderRadius: 10, padding: 8 }}>
                  <BarChart3 size={18} color="var(--purple)" strokeWidth={2.2} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Statutory Deductions</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <ComplianceRow label="SHIF Contributions" value={`KES ${totalShif.toLocaleString()}`} accent="blue" onClick={() => generateReport('shif')} />
                <ComplianceRow label="NSSF Contributions" value={`KES ${totalNssf.toLocaleString()}`} accent="purple" onClick={() => generateReport('nssf')} />
                <ComplianceRow label="PAYE Tax" value={`KES ${totalPaye.toLocaleString()}`} accent="red" onClick={() => generateReport('paye')} />
                <ComplianceRow label="Housing Levy" accent="amber" onClick={() => generateReport('housing')} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── PROCESS MODAL ── */}
      {showProcessModal && (
        <Modal title="Process Payroll" onClose={() => setShowProcessModal(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <FormField label="Payroll Month">
              <MonthPicker
                value={selectedMonth}
                onChange={setSelectedMonth}
                label="Select month"
                style={{ width: '100%', minWidth: 'unset' }}
              />
            </FormField>

            <FormField label="NSSF Tier">
              <select
                value={nssfTier}
                onChange={e => setNssfTier(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
                onFocus={e => { e.target.style.borderColor = 'var(--blue)'; e.target.style.boxShadow = '0 0 0 3px rgba(67,97,238,.12)'; }}
                onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
              >
                <option value="both">Both Tiers</option>
                <option value="tier1">Tier 1 Only</option>
                <option value="tier2">Tier 2 Only</option>
              </select>
            </FormField>

            <div style={{ background: 'var(--blue-light)', borderRadius: 12, padding: '14px 18px' }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--blue)', margin: '0 0 8px', letterSpacing: '.04em', textTransform: 'uppercase' }}>Processing will include</p>
              {['PAYE calculation per KRA rates', 'SHIF deductions (2.75%)', 'NSSF contributions (2025 rates)', 'Housing Levy (1.5%)', 'Personal relief (KES 2,400)'].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                  <CheckCircle size={13} color="var(--blue)" strokeWidth={2.5} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>{item}</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
              <button
                onClick={() => setShowProcessModal(false)}
                style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--white)', fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={processPayroll}
                disabled={processing}
                style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: 'none', background: processing ? 'var(--border)' : 'var(--blue)', fontSize: 13, fontWeight: 700, color: processing ? 'var(--text-muted)' : '#fff', cursor: processing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              >
                {processing && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                {processing ? 'Processing…' : 'Process Payroll'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── REPORTS MODAL ── */}
      {showReportsModal && (
        <Modal title="Generate Reports" onClose={() => setShowReportsModal(false)}>
          {generating ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Loader2 size={28} color="var(--blue)" style={{ animation: 'spin 1s linear infinite', display: 'block', margin: '0 auto 12px' }} />
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', fontWeight: 600 }}>Generating report, please wait…</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { name: 'P9 Forms', desc: 'Annual tax certificates', type: 'p9' },
                { name: 'Payroll Summary', desc: 'Complete payroll summary', type: 'summary' },
                { name: 'PAYE Schedule', desc: 'Monthly PAYE summary', type: 'paye' },
                { name: 'SHIF Report', desc: 'SHIF contributions report', type: 'shif' },
                { name: 'NSSF Report', desc: 'NSSF contributions report', type: 'nssf' },
                { name: 'Housing Levy Report', desc: 'Housing levy contributions', type: 'housing' },
                { name: 'NITA Reports', desc: 'NITA contributions report', type: 'nita' },
              ].map(report => (
                <div
                  key={report.type}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '14px 16px', borderRadius: 12, border: '1.5px solid var(--border)',
                    transition: 'border-color .15s, background .15s', cursor: 'default',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--blue)'; e.currentTarget.style.background = 'var(--blue-light)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'transparent'; }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{report.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginTop: 2 }}>{report.desc}</div>
                  </div>
                  <PrimaryBtn size="sm" onClick={() => generateReport(report.type)} disabled={generating}>View</PrimaryBtn>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default KenyanPayroll;